import { useRef, useCallback, useState } from 'react';
import { dialParty, inviteParty, MediatorServiceError, type PhoneJoin, type SessionStatus } from '../api/mediatorService';
import { DialInBackup } from '../components/DialInBackup';
import { EvidenceList } from '../components/EvidenceList';
import { NegotiationHistory } from '../components/NegotiationHistory';
import { PartyCard, type InviteState } from '../components/PartyCard';
import { PolicyConstraints } from '../components/PolicyConstraints';
import { SurfacedFocus } from '../components/SurfacedFocus';
import { SituationSection } from '../components/SituationSection';
import { StartSessionButton } from '../components/StartSessionButton';
import type { Case, Party } from '../types/case';

interface Props {
  sessionId: string;
  meetingId: string;
  accessToken: string;
  case: Case;
  sessionStatus: SessionStatus;
  phoneJoin?: PhoneJoin | null;
  onStartSession: () => void;
  starting: boolean;
  startError?: string;
}

const startButtonLabel = (status: SessionStatus): string => {
  switch (status) {
    case 'consenting':
      return 'Resume session ▶';
    case 'in_progress':
      return 'Session in progress';
    case 'ended':
      return 'Session ended';
    default:
      return 'Start session ▶';
  }
};

export function BriefingPage({
  sessionId,
  meetingId,
  accessToken,
  case: c,
  sessionStatus,
  phoneJoin,
  onStartSession,
  starting,
  startError,
}: Props) {
  const [invites, setInvites] = useState<Record<string, InviteState>>({});
  const [dialing, setDialing] = useState<Record<string, string>>({});
  const pendingDialRef = useRef<string | null>(null);

  // Zoom calls the party's phone and joins them to the meeting (needs the
  // Premium Audio add-on; errors from Zoom are shown verbatim).
  const handleDial = useCallback(
    async (party: Party) => {
      // Inline confirm — window.confirm is silently blocked inside the
      // Zoom webview (Danny, live test 2026-09-14: the button did nothing).
      if (pendingDialRef.current !== party.id) {
        pendingDialRef.current = party.id;
        setDialing((prev) => ({ ...prev, [party.id]: `Call ${party.phone}? Press again to place the call.` }));
        window.setTimeout(() => {
          if (pendingDialRef.current === party.id) {
            pendingDialRef.current = null;
            setDialing((prev) => (prev[party.id]?.startsWith('Call ') ? { ...prev, [party.id]: '' } : prev));
          }
        }, 8_000);
        return;
      }
      pendingDialRef.current = null;
      setDialing((prev) => ({ ...prev, [party.id]: 'Calling…' }));
      try {
        await dialParty(sessionId, party.id, accessToken);
        setDialing((prev) => ({ ...prev, [party.id]: `📞 Zoom is calling ${party.phone}` }));
        window.setTimeout(() => setDialing((prev) => ({ ...prev, [party.id]: '' })), 15_000);
      } catch (err) {
        const message =
          err instanceof MediatorServiceError ? err.message : err instanceof Error ? err.message : 'Call failed';
        setDialing((prev) => ({ ...prev, [party.id]: `⚠ ${message}` }));
      }
    },
    [sessionId, accessToken],
  );

  const handleInvite = useCallback(
    async (party: Party) => {
      setInvites((prev) => ({ ...prev, [party.id]: { status: 'generating' } }));
      try {
        const result = await inviteParty(
          { sessionId, partyId: party.id, partyName: party.name, meetingId },
          accessToken,
        );
        setInvites((prev) => ({
          ...prev,
          [party.id]: { status: 'ready', joinUrl: result.join_url },
        }));
        // Pre-copy so the mediator can paste immediately into the outreach tool.
        try {
          await navigator.clipboard.writeText(result.join_url);
        } catch {
          /* clipboard denied inside the iframe — the Copy button on the card
             still works because it's tied to a user gesture */
        }
      } catch (err) {
        const message =
          err instanceof MediatorServiceError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Invite failed';
        setInvites((prev) => ({
          ...prev,
          [party.id]: { status: 'failed', error: message },
        }));
      }
    },
    [sessionId, meetingId, accessToken],
  );

  const meta = c.metadata as { property_name?: string; court_case_no?: string };
  const caseRef = meta.court_case_no ? `CASE ${meta.court_case_no}` : 'MEDIATION BRIEFING';
  const propertyName = meta.property_name ?? 'Mediation briefing';

  return (
    <div className="space-y-3 p-3">
      <header className="px-1">
        <div className="font-mono text-xs uppercase tracking-wider text-gray-500">{caseRef}</div>
        <h1 className="mt-0.5 text-lg font-semibold">{propertyName}</h1>
        <div className="mt-0.5 text-xs text-gray-500">
          Status: <span className="font-medium text-gray-700">{c.status}</span> · Priority:{' '}
          <span className="font-medium text-gray-700">{c.priority}</span>
        </div>
      </header>


      <section className="space-y-2">
        <div className="section-label px-1">Parties</div>
        {c.parties.map((party) => (
          <div key={party.id}>
            <PartyCard
              party={party}
              invite={invites[party.id] ?? { status: 'idle' }}
              onInvite={handleInvite}
            />
            {party.phone && (
              <div className="mt-0.5 flex items-center gap-2 px-1">
                <button
                  type="button"
                  className={
                    dialing[party.id]?.startsWith('Call ')
                      ? 'rounded bg-brand px-1.5 text-xs font-medium text-white'
                      : 'text-xs text-gray-400 hover:text-brand'
                  }
                  title="Zoom calls their phone and joins them to this meeting (audio only — transcription still works)"
                  onClick={() => void handleDial(party)}
                >
                  📞 {dialing[party.id]?.startsWith('Call ') ? 'Confirm — place the call' : 'Call them into the meeting'}
                </button>
                {dialing[party.id] && <span className="text-xs text-gray-600">{dialing[party.id]}</span>}
              </div>
            )}
          </div>
        ))}
      </section>

      <DialInBackup meetingId={meetingId} phoneJoin={phoneJoin} />
      <SituationSection case={c} />
      <NegotiationHistory recommendations={c.recommendations} />
      <SurfacedFocus recommendations={c.recommendations} />
      <PolicyConstraints case={c} />
      <EvidenceList evidence={c.evidence} />

      <section className="space-y-2 pt-1">
        <StartSessionButton
          onStart={onStartSession}
          working={starting}
          disabled={sessionStatus === 'in_progress' || sessionStatus === 'ended'}
          label={startButtonLabel(sessionStatus)}
        />
        {startError && (
          <div className="text-center text-xs text-red-600">{startError}</div>
        )}
      </section>
    </div>
  );
}
